import { useAppSelector } from 'app/hooks';
import type { DuctedPanelSelection } from 'components/equipment-page/ducted-system-detail-panel';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { withDuctedCompareIdentity } from 'utils/ducted-compare-identity';
import { selectDuctedPairLoads } from 'utils/ducted-pair-context';
import { buildSelectedCombination } from 'utils/quotable-add-pipeline';
import { resolveTonnageSeed } from 'utils/tonnage-logic';
import useQuotableAdd from './use-quotable-add';
import {
  DuctedCombinationAddedHandler,
  PendingDuctedCombinationAdd
} from './use-ducted-combination-add-types';

type UseDuctedCombinationAddParams = {
  onAdded: DuctedCombinationAddedHandler;
  onSettled?: (context: DuctedPanelSelection) => void;
};

export default function useDuctedCombinationAdd({
  onAdded,
  onSettled
}: UseDuctedCombinationAddParams) {
  const existingSystems = useAppSelector(
    (state) => state.structure.existingSystems
  );
  const recommendationSelectionsBySystem = useAppSelector(
    (state) => state.structure.recommendationSelectionsBySystem
  );
  const instanceUniqueId = useAppSelector(
    (state) => state.project.instanceUniqueId
  );
  const [pendingAdd, setPendingAdd] =
    useState<PendingDuctedCombinationAdd | null>(null);
  const requestSeqRef = useRef(0);
  const coolingBtu = useAppSelector(
    (state) =>
      selectDuctedPairLoads(state, pendingAdd?.context.systemUniqueId).cooling
  );

  // Same live-Redux components source as the detail panel, keyed off the
  // CLICKED card's system — cards from multiple existing systems can render
  // in one host.
  const components = useMemo(
    () =>
      pendingAdd
        ? existingSystems.find(
            (s) => s.uniqueId === pendingAdd.context.systemUniqueId
          )?.components ?? []
        : [],
    [existingSystems, pendingAdd]
  );

  // Same shared resolver on the same Redux inputs as the CCB-check
  // dispatchers (AHA-6796), keyed off the CLICKED card's system: forwarded
  // into Phase 6 so the server can compute tonnage-estimate ducted coverage.
  const tonnageSeed = useMemo(
    () =>
      resolveTonnageSeed({
        record: pendingAdd
          ? recommendationSelectionsBySystem[pendingAdd.context.systemUniqueId]
              ?.tonnage ?? null
          : null,
        components,
        coolingBtu
      }),
    [pendingAdd, recommendationSelectionsBySystem, components, coolingBtu]
  );

  const { beginAdd, accessorySelection } = useQuotableAdd({
    instanceUniqueId,
    components,
    existingSystemUniqueId: pendingAdd?.context.systemUniqueId,
    accessoryData: pendingAdd?.context.accessoryData ?? {},
    tonnageSeed,
    onSuccess: (added) => {
      const context = pendingAdd?.context;
      if (!context) return;
      onAdded(
        added.map((system) => withDuctedCompareIdentity(system, context)),
        context
      );
    },
    onRunSettled: () => {
      const context = pendingAdd?.context;
      if (!context) return;
      onSettled?.(context);
    }
  });

  // Effect-driven add: fires once per request AFTER the re-render that baked
  // the clicked card's context into the hook's closures. `pendingAdd`
  // deliberately stays set afterward so the hook's accessoryData closure
  // remains correct while the AccessorySelectionModal is open; the next
  // request overwrites it.
  const consumedRequestIdRef = useRef(0);
  useEffect(() => {
    if (
      pendingAdd === null ||
      pendingAdd.requestId === consumedRequestIdRef.current
    ) {
      return;
    }
    consumedRequestIdRef.current = pendingAdd.requestId;
    const { combination, productData, keptPairing, displayTypeLabel } =
      pendingAdd.context;
    beginAdd([
      buildSelectedCombination(
        combination,
        productData,
        keptPairing,
        displayTypeLabel
      )
    ]);
    // beginAdd is recreated every render; the consumed-requestId guard makes
    // the extra runs no-ops.
  }, [pendingAdd, beginAdd]);

  const requestAdd = useCallback((context: DuctedPanelSelection) => {
    requestSeqRef.current += 1;
    setPendingAdd({ requestId: requestSeqRef.current, context });
  }, []);

  return { requestAdd, accessorySelection };
}
